import React, { useState, createContext } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Header from "./components/Header/Header.js";
import Footer from "./components/Footer/Footer.js";
import { useConfiguration, useHomePageContent } from "./hooks/useQueries.js";
import { useMediaQuery } from "react-responsive";
import { createContactsArray } from "./functions/utils.js";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { defaultLanguage } from "./multilanguage.js";

import SearchResults from "./pages/SearchResults.js";
import Category from "./pages/Category.js";
import Cart from "./components/Cart/Cart.js";
import Home from "./pages/Home.js";
import Store from "./pages/Store.js";
import Blog from "./pages/Blog.js";
import NotFound from "./pages/NotFound.js";
import Product from "./pages/Product.js";
import Page from "./pages/Page.js";
import Checkout from "./pages/Checkout.js";
import AdminLogin from "./pages/AdminLogin.js";
import AdminOrders from "./pages/AdminOrders.js";
import Loading from "./components/Loading/Loading.js";

export const ConfigContext = createContext();
export const NavigationContext = createContext();
export const TranslateContext = createContext();

/**
 * React Query client configuration.
 * Configures global caching and retry behavior.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2, // Retry failed queries twice
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

/**
 * Inner app component that uses React Query hooks.
 * Separated to ensure QueryClientProvider is available.
 */
function AppContent() {
  const isMobile = useMediaQuery({ maxWidth: 1023 });
  const [language, setLanguage] = useState(defaultLanguage);

  // Use React Query hooks for cached data fetching
  const { data: configData, isLoading: configLoading } = useConfiguration();
  const { data: homePageContent, isLoading: homeLoading } = useHomePageContent();

  // Derived state from configuration
  const configuration = configData?.configuration || {};
  const navigation = configData?.navigation || [];
  const pageTypesSlugs = configData?.allPages || [];
  const headerLogo = configuration?.headerLogo?.asset?.url || "";
  const footerLogo = configuration?.footerLogo?.asset?.url || "";
  const contacts = createContactsArray(configuration?.businessInfo);

  function CartWrapper() {
    const location = useLocation();
    return location.pathname !== "/checkout" && !location.pathname.startsWith("/admin") ? <Cart /> : null;
  }

  const helmetContext = {};

  // Show loading state while configuration is being fetched
  if (configLoading) {
    return <Loading />;
  }

  return (
    <BrowserRouter>
      <TranslateContext.Provider value={{ language, setLanguage }}>
        <HelmetProvider context={helmetContext}>
          <ConfigContext.Provider value={configuration}>
            <NavigationContext.Provider value={navigation}>
              <CartWrapper />
              <div className="app" style={{ backgroundColor: configuration?.colors?.defaultBackground?.hex }}>
                <Header
                  mainColor={configuration?.colors?.main?.hex}
                  secondaryColor={configuration?.colors?.secondary?.hex}
                  bgColor={"white"}
                  logoSrc={headerLogo}
                  menu={navigation}
                  isMobile={isMobile}
                  mobileMenuWidth={"100%"}
                />
                <Routes>
                  <Route 
                    element={
                      <Home 
                        content={homeLoading ? null : homePageContent} 
                        configuration={configuration} 
                      />
                    } 
                    path="/" 
                    exact 
                  />
                  <Route path="/search" element={<SearchResults />} />
                  <Route element={<Checkout />} path="/checkout" />
                  <Route element={<AdminLogin />} path="/admin/login" />
                  <Route element={<AdminOrders />} path="/admin" />
                  {pageTypesSlugs.length > 0 &&
                    pageTypesSlugs.map((page) => {
                      let Element;
                      switch (page._type) {
                        case "store":
                          Element = Store;
                          break;
                        case "blog":
                          Element = Blog;
                          break;
                        case "product":
                          Element = Product;
                          break;
                        case "blogpost":
                        case "page":
                          Element = Page;
                          break;
                        case "productCategory":
                        case "blogCategory":
                          Element = Category;
                          break;
                        default:
                          Element = NotFound;
                      }
                      return <Route key={page.slug} exact path={`/${page.slug}`} element={<Element />} />;
                    })}
                  <Route exath path="/privacy-policy" element={<Page />} />
                  <Route exath path="/terms-and-conditions" element={<Page />} />
                  <Route path={pageTypesSlugs.length > 0 ? "*" : ""} element={<NotFound />} />
                </Routes>
                <Footer
                  iconColor={configuration?.colors?.secondary?.hex}
                  bgColor={configuration?.colors?.main?.hex}
                  textColor={"white"}
                  logoSrc={footerLogo}
                  contacts={contacts}
                  menu={navigation}
                  copyrightText={configuration?.rightsReserved?.[language]}
                  copyrightTextBackgroundColor={configuration?.colors?.secondary?.hex}
                  hasNewsletter={configuration?.hasNewsletter}
                  newsLetterCTA={
                    "Запиши се за нашия бюлетин, за да получаваш преждевременни известия за всички новости в сайта и магазина!"
                  }
                  newsletterButtonColor={"#98DDF4"}
                  newsletterButtonHoverColor={"#E4F3FD"}
                  checkboxColor={configuration?.colors?.secondary?.hex}
                  privacyPolicyURL={"/privacy-policy"}
                />
              </div>
            </NavigationContext.Provider>
          </ConfigContext.Provider>
        </HelmetProvider>
      </TranslateContext.Provider>
    </BrowserRouter>
  );
}

/**
 * Main App component.
 * Wraps the application with QueryClientProvider for React Query caching.
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}

export default App;
